import {Component, HostBinding, HostListener, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import { ReportPageInterface } from 'src/app/interface';
import { FieldGroupValue, FieldList, FieldValue, IncomeStatementWeightCalc, Report, WeightCalc, WeightCalcDTO } from 'src/app/model';
import { FinancialType, FooterButtonOption, PageNumbers } from 'src/app/model/Enums';
import { ReportProvider } from 'src/app/providers';
import {CommonService, ReportService, ToasterService} from 'src/app/services';
import { environment } from 'src/environments/environment';
import {ChartConfiguration} from 'chart.js';
import {CurrencyPipe} from '../../../../../pipes';
import {routeTransition} from '../../../../../animations/route-transition';

@Component({
  selector: 'app-weighting-table',
  templateUrl: './weighting-table.component.html',
  styleUrls: ['./weighting-table.component.scss'],
  animations: [routeTransition],
})
export class WeightingTableComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  // new weighting logic
  weightResultDetails: WeightCalcDTO = null;
  chartData = [];
  chartLabels = [];
  chartColors = [
    {backgroundColor: document.documentElement.style.getPropertyValue('--primary-color')},
    {backgroundColor: document.documentElement.style.getPropertyValue('--disabled-color')}
  ];

  barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    /*tooltips: {
      callbacks: {
        label: function(tooltipItem, data) {
          var value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          let [integer] = (value || "0").toString().split('.');
          integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return data.datasets[tooltipItem.datasetIndex].label + ': ' + integer;
        }
      } // end callbacks:
    }, //end tooltips
    legend: {
      display: false
    },
    scales: {
      xAxes: [{
        ticks: {
          display: false
        }
      }],
      yAxes: [{
        ticks: {
          beginAtZero: true,
          userCallback: function(value, index, values) {
            let [integer] = (value || "0").toString().split('.');
            integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return integer;
          }
        }
      }]
    }*/
  };

  chartDataNew: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: []
  };

  renderChart: boolean = false;
  outlierMode: boolean = false;
  noYearsRender: boolean = false;

  isEditMode: boolean = false;
  financialTypeEnum = FinancialType;

  showWeightingDebug: boolean = false;
  weightingDebugInformation: WeightCalc;

  chartVisible: boolean = true;
  outlierVisible: boolean = true;
  weightingsVisible: boolean = true;

  constructor(
    public reportService: ReportService,
    private toastr: ToasterService,
    private router: Router,
    private reportProvider: ReportProvider,
    public currencyPipe: CurrencyPipe,
    private common: CommonService,
  ) { }

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport && this.currentReport.LATEST) {
        if (this.currentReport.LATEST.INCOME_STATEMENT.length) {
          this.getWeightingDetails();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   * @param obj income statement of weight calculation which year we want to update
   */
  callUpdateAPI(fieldName: string, obj: IncomeStatementWeightCalc) {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'INCOME_STATEMENT';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = obj[fieldName];
    this.reportProvider.updateReportField(obj.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        if (fieldName == 'REVENUE') {
          const incomeStatementIndex = this.currentReport.LATEST.INCOME_STATEMENT.findIndex(x => x.ID === obj.ID);
          if (incomeStatementIndex != -1) {
            // for income statement
            this.currentReport.LATEST.INCOME_STATEMENT[incomeStatementIndex][fieldName] = obj[fieldName];
          }
          this.getWeightingDetails();
        }
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  onPercentageFocus(obj: IncomeStatementWeightCalc) {
    obj.WEIGHT = obj.WEIGHT ? Number(obj.WEIGHT) : null;
  }

  /**
   * @description for group field update
   * @param obj income statement with weight calculation
   */
  callUpdateGroupAPI(obj: IncomeStatementWeightCalc) {
    let objFieldArray = new Array<FieldList>();
    objFieldArray.push({ FIELD: 'WEIGHT', VALUE: 0 });
    objFieldArray.push({ FIELD: 'OUTLIER', VALUE: obj.OUTLIER });

    let objFieldGroup = new FieldGroupValue();
    objFieldGroup.GROUP = 'INCOME_STATEMENT';
    objFieldGroup.FIELD_LIST = objFieldArray;

    this.reportProvider.updateReportFieldGroup(obj.ID, objFieldGroup).subscribe(res => {
      if (res.success) {
        this.renderChartView();
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /* callDummyUpdateAPI() {
    this.reportProvider.updateWS(this.currentReport.LATEST.ID, 'WEIGHTING').subscribe(res => {
      if (res.success) {
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  } */

  /**
   * @description for form validation
   * @returns check form is valid or not
   */
  checkValidation() {
    if (this.weightResultDetails?.WEIGHT_CALC_LIST.reduce((summ, v) => (summ += Number(v.WEIGHT) || 0), 0) == 100) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * @description for get weighting details
   */
  getWeightingDetails() {
    this.reportProvider.getWeighting(this.currentReport.LATEST.ID).subscribe(res => {
      if (res.success) {

        this.weightResultDetails = res.result;

        this.weightResultDetails?.WEIGHT_CALC_LIST.forEach(element => {
          const IS_Object = this.currentReport.LATEST.INCOME_STATEMENT.find(el => el.ID === element.ID);
          element.FINANCIAL_TYPE = IS_Object?.FINANCIAL_TYPE;
          element.MONTHS = IS_Object?.MONTHS;
          element.WEIGHT = element.PERCENTAGE;
        });

        this.weightResultDetails?.WEIGHT_CALC_LIST.sort((a, b) => Number(a.YEAR) - Number(b.YEAR));

        this.weightingDebugInformation = WeightCalc.parse(this.weightResultDetails.DEBUG_INFO);

        this.renderChartView();
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description for render chart
   */
  renderChartView(): void {
    if(this.weightResultDetails?.WEIGHT_CALC_LIST?.length){
      this.renderChart = false;

      this.chartDataNew.datasets = [];
      this.chartDataNew.labels = [];
      this.chartData = [];
      this.chartLabels = [];

      // Filter out Years (not filled, selected as outlier)
      if(this.weightResultDetails?.WEIGHT_CALC_LIST?.length > 0){
        let dataRev = {data: [], label: 'Revenue', backgroundColor: [], hoverBackgroundColor: []};
        let dataSde = {data: [], label: 'SDE', backgroundColor: [], hoverBackgroundColor: []};

        for(let i=0; i < this.weightResultDetails.WEIGHT_CALC_LIST.length; i++){
          dataRev.data.push(this.weightResultDetails.WEIGHT_CALC_LIST[i].REVENUE);
          dataSde.data.push(this.weightResultDetails.WEIGHT_CALC_LIST[i].SDE);

          const primaryColor = document.documentElement.style.getPropertyValue('--primary-color');
          const disabledColor = document.documentElement.style.getPropertyValue('--disabled-color');
          const secondaryColor = document.documentElement.style.getPropertyValue('--secondary-color');
          if (this.weightResultDetails.WEIGHT_CALC_LIST[i].OUTLIER) {
            dataRev.backgroundColor.push(secondaryColor);
            dataSde.backgroundColor.push(secondaryColor);
            dataRev.hoverBackgroundColor.push(secondaryColor);
            dataSde.hoverBackgroundColor.push(secondaryColor);
          } else {
            dataRev.backgroundColor.push('#000000');
            dataSde.backgroundColor.push(primaryColor);
            dataRev.hoverBackgroundColor.push('#000000');
            dataSde.hoverBackgroundColor.push(primaryColor);
          }
          this.chartLabels.push(this.weightResultDetails.WEIGHT_CALC_LIST[i].YEAR.toString());
          this.chartDataNew.labels.push(this.weightResultDetails.WEIGHT_CALC_LIST[i].YEAR.toString());
        }

        this.chartDataNew.datasets.push(dataRev);
        this.chartDataNew.datasets.push(dataSde);
        this.chartData.push(dataRev);
        this.chartData.push(dataSde);
        this.noYearsRender = false;

      } else {
        this.noYearsRender = true;
      }

      this.renderChart = true;
    }
  }

  /**
   * @description change auto/manual calc
   */
  changeAutomaticallyManually() {
    this.currentReport.LATEST.WEIGHTINGS_ADVANCED = !this.currentReport.LATEST.WEIGHTINGS_ADVANCED;
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'REPORT_VERSION';
    objFieldValue.FIELD = 'WEIGHTINGS_ADVANCED';
    objFieldValue.VALUE = this.currentReport.LATEST.WEIGHTINGS_ADVANCED.toString();
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        this.getWeightingDetails();
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description get previous year revenue
   * @param index position
   * @returns return previous year revenue
   */
  getPreviousYearRevenue(index: number) {
    if (index === 0)
      return 0;
    return this.weightResultDetails?.WEIGHT_CALC_LIST[index - 1].REVENUE || 0;
  }

  /**
   * @description get previous year SDE
   * @param index position
   * @returns return previous year SDE
   */
  getPreviousYearSDE(index: number) {
    if (index === 0)
      return 0;
    return this.weightResultDetails?.WEIGHT_CALC_LIST[index - 1].SDE || 0;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    //  && this.debugInformation.length
    if (environment.SHOW_DEBUG && ($event.ctrlKey || $event.metaKey) && ($event.keyCode == 68 || $event.keyCode == 81)) {
      this.showWeightingDebug = !this.showWeightingDebug;
    }
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return (this.weightResultDetails?.WEIGHT_CALC_LIST && this.checkValidation()) || this.currentReport?.isLocked;
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return false;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK) {
      this.reportService.updateMaxStage(PageNumbers.WEIGHTING_TABLE);
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/assets-liabilities`);
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/adjustments`);
    } else if (buttonOption == FooterButtonOption.SKIP_CLICK) {
      this.isEditMode = !this.isEditMode;
    }
  }

  /** check max cash flow value */
  get isCashFlowOverFifteenMillion(): boolean{
    return (this.currentReport?.LATEST?.WEIGHTED_SDE > this.common.maxCashflow);
  }
}
